<template>
  <div>
    <!-- Sidebar fixa para páginas autenticadas -->
    <div v-if="showAppChrome" class="min-h-screen" style="background-color: var(--bg-primary); color: var(--text-primary);">
      <div class="flex">
        <!-- Sidebar de workspaces (estilo Discord) - FIXA -->
        <WorkspaceSidebar
          :workspaces="workspaces"
          :currentWorkspace="currentWorkspace"
          :isHome="isHome"
          @select-workspace="selectWorkspace"
          @go-home="goHome"
          @open-create-modal="openCreateModal"
          @reorder-workspaces="handleReorderWorkspaces"
          @edit-workspace="openEditModal"
          @delete-workspace="handleDeleteWorkspace"
          @open-profile="openProfileModal"
        />

        <!-- Submenu de Workspace - DINÂMICO -->
        <WorkspaceSubmenu
          :currentWorkspace="currentWorkspace"
          :isHome="isHome"
        />

        <!-- Área principal - MUDANÇA DINÂMICA -->
        <div class="flex-1 main-content main-content--blocked-aware">
          <AccountBlockedInPage />
          <router-view :key="mainRouterViewKey" />
        </div>
      </div>

      <AccountBlockedModal v-model="accountBlockModalVisible" />

      <!-- Modais de Workspace - GLOBAIS -->
      <CreateWorkspaceModal 
        v-if="showCreateModal"
        @close="closeCreateModal"
        @workspace-created="onWorkspaceCreated"
      />
      
      <EditWorkspaceModal 
        v-if="showEditModal"
        :workspace="workspaceToEdit"
        @close="closeEditModal"
        @updated="onWorkspaceUpdated"
      />

      <!-- Modal de Perfil - GLOBAL -->
      <ProfileModal 
        v-if="showProfileModal"
        @close="closeProfileModal"
      />
    </div>

    <!-- Páginas de login/register sem sidebar -->
    <div v-else>
      <router-view />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, provide, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useThemeStore } from './stores/theme'
import { useAuthStore } from './stores/auth'
import { useWorkspaceStore } from './stores/workspace'
import { useGlobalWorkspaces } from './composables/useGlobalWorkspaces'
import WorkspaceSidebar from './components/WorkspaceSidebar.vue'
import WorkspaceSubmenu from './components/WorkspaceSubmenu.vue'
import CreateWorkspaceModal from './components/CreateWorkspaceModal.vue'
import EditWorkspaceModal from './components/EditWorkspaceModal.vue'
import ProfileModal from './components/ProfileModal.vue'
import AccountBlockedModal from './components/account/AccountBlockedModal.vue'
import AccountBlockedInPage from './components/account/AccountBlockedInPage.vue'
import { useAccountAccessBlock } from './composables/useAccountAccessBlock'
import { usePlanoFeatures } from './composables/usePlanoFeatures'
import Swal from 'sweetalert2'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const themeStore = useThemeStore()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const { modalVisible: accountBlockModalVisible, openModal: openAccountBlockModal, checkRouteOnLoad, closeInPageBlock } = useAccountAccessBlock()
const planoFeaturesForRoute = usePlanoFeatures()

/** Se sessão cair em rota protegida (ex.: race no logout), ir para login sem F5. */
watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (!authenticated && route.meta.requiresAuth) {
      router.replace('/login')
    }
  }
)

watch(
  () => authStore.isAccessBlocked,
  (blocked) => {
    if (blocked && authStore.isAuthenticated && !authStore.isSuperAdmin) {
      openAccountBlockModal()
    }
  },
  { immediate: true }
)

watch(
  () => route.fullPath,
  async (path) => {
    if (!authStore.isAuthenticated || authStore.isSuperAdmin) {
      closeInPageBlock()
      return
    }
    const wsMatch = path.match(/\/workspace\/(\d+)\//)
    if (wsMatch) {
      await planoFeaturesForRoute.loadForWorkspace(Number(wsMatch[1]))
    }
    checkRouteOnLoad(path)
  }
)

/** Remonta a página ao trocar de workspace na mesma rota (ex.: usuários → usuários). */
const mainRouterViewKey = computed(() => {
  const id = route.params.id
  if (id != null && id !== '' && route.path.startsWith('/workspace/')) {
    return `${id}::${String(route.name ?? route.path)}`
  }
  return route.fullPath
})

/** Formulário público e telas sem chrome (sem sidebar/submenu). */
const showAppChrome = computed(() => {
  return authStore.isAuthenticated && !route.meta.publicForm
})

// Usar workspaces globais
const { 
  workspaces, 
  currentWorkspace, 
  isHome, 
  selectWorkspace, 
  goHome, 
  updateWorkspaceInList,
  removeWorkspaceFromList,
  loadWorkspaces
} = useGlobalWorkspaces()

// Modais de workspace globais
const showCreateModal = ref(false)
const showEditModal = ref(false)
const workspaceToEdit = ref(null)

// Modal de perfil global
const showProfileModal = ref(false)

const isLightTheme = computed(() => themeStore.isLight)

// Funções dos modais de workspace
const openCreateModal = () => {
  showCreateModal.value = true
}

provide('openCreateWorkspaceModal', openCreateModal)

const closeCreateModal = () => {
  showCreateModal.value = false
}

const openEditModal = (workspace) => {
  workspaceToEdit.value = workspace
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  workspaceToEdit.value = null
}

const handleReorderWorkspaces = (reorderedWorkspaces) => {
  workspaceStore.reorderWorkspaces(reorderedWorkspaces)
}

const handleDeleteWorkspace = async (workspace) => {
  console.log('🗑️ Iniciando exclusão do workspace:', workspace)
  
  // Confirmar exclusão com SweetAlert2
  const result = await Swal.fire({
    title: t('app.delete.title'),
    html: t('app.delete.confirmHtml', { name: workspace.nome }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: t('app.delete.confirmButton'),
    cancelButtonText: t('app.delete.cancelButton'),
    reverseButtons: true
  })
  
  if (!result.isConfirmed) {
    console.log('❌ Exclusão cancelada pelo usuário')
    return
  }
  
  console.log('✅ Usuário confirmou exclusão')
  console.log('🔄 Deletando no backend - ID:', workspace.id)
  
  // Deletar no backend e aguardar resposta
  const deleteResult = await workspaceStore.deleteWorkspace(workspace.id)
  
  console.log('📊 Resultado da exclusão:', deleteResult)
  
  if (deleteResult.success) {
    console.log('✅ Backend confirmou exclusão, removendo da sidebar...')
    
    // 1. Remover do localStorage
    const savedOrder = localStorage.getItem(`workspace-order-${authStore.user?.id}`)
    if (savedOrder) {
      try {
        const order = JSON.parse(savedOrder)
        const newOrder = order.filter(id => id !== workspace.id)
        localStorage.setItem(`workspace-order-${authStore.user?.id}`, JSON.stringify(newOrder))
        console.log('✅ Ordenação atualizada no localStorage')
      } catch (e) {
        console.error('Erro ao atualizar ordenação:', e)
      }
    }
    
    // 2. Remover da lista de workspaces
    console.log('🗑️ Lista antes:', workspaces.value.map(w => w.id))
    removeWorkspaceFromList(workspace.id)
    await nextTick()

    goHome()
  } else {
    console.error('❌ Falha ao deletar workspace:', deleteResult.error)
  }
}

// Funções do modal de perfil
const openProfileModal = () => {
  showProfileModal.value = true
}

const closeProfileModal = () => {
  showProfileModal.value = false
}

// Callbacks dos modais
const onWorkspaceCreated = (newWorkspace) => {
  console.log('🎉 Novo workspace criado:', newWorkspace)
  // Adicionar workspace à lista global
  workspaces.value.push(newWorkspace)
  console.log('📋 Total de workspaces agora:', workspaces.value.length)
  closeCreateModal()
}

const onWorkspaceUpdated = (updatedWorkspace) => {
  // Atualizar workspace na lista global
  const index = workspaces.value.findIndex(w => w.id === updatedWorkspace.id)
  if (index !== -1) {
    workspaces.value[index] = updatedWorkspace
  }
  closeEditModal()
}

onMounted(async () => {
  console.log('=== [APP] App.vue montado ===')
  console.log('[APP] Tema:', isLightTheme.value ? 'claro' : 'escuro')
  
  // Inicializar autenticação (restaurar sessão do localStorage se existir)
  await authStore.initializeAuth()
  
  console.log('[APP] isAuthenticated:', authStore.isAuthenticated)
  console.log('[APP] user:', authStore.user?.email)
  
  // Se usuário estiver autenticado após inicialização, carregar workspaces
  if (authStore.isAuthenticated) {
    console.log('[APP] ✅ Usuário autenticado')
    console.log('[APP] 📡 Carregando workspaces DO BANCO DE DADOS...')
    await loadWorkspaces()
  } else {
    console.log('[APP] ℹ️ Usuário não autenticado')
  }
  
  console.log('=== [APP] Fim do mounted ===')
})
</script>

<style>
#app {
  min-height: 100vh;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.main-content {
  margin-left: 240px; /* Apenas a largura do submenu (sidebar não conta) */
  position: relative;
  transition: margin-left 0.3s ease;
  height: 100vh;
  overflow-y: auto;
}

.main-content--blocked-aware {
  min-height: 100vh;
}

/* Responsividade */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
  
  .accept-invite-page .main-content {
    margin-left: 0 !important;
  }
}
</style>
